/**
 * ActivityTracker - Tracks per-peer input activity and detects AFK state.
 *
 * Replaces the legacy playerActivity.ts timer system. Called by InputManager
 * on each valid input message. Manages per-peer AFK timers and optional
 * auto-kick after extended inactivity.
 *
 * Responsibilities:
 * - Track last activity timestamp per peer
 * - Detect AFK after configurable timeout
 * - Optionally kick peers after extended AFK
 * - Emit callbacks on AFK state transitions
 */

export type ActivityTrackerConfig = {
  afkTimeoutMs: number;
  kickTimeoutMs?: number;
  onAfkChanged?: (peerId: string, afk: boolean) => void;
  onKick?: (peerId: string) => void;
};

type PeerActivity = {
  afk: boolean;
  lastActivityAt: number;
  afkTimer: ReturnType<typeof setTimeout> | null;
  kickTimer: ReturnType<typeof setTimeout> | null;
};

const DEFAULT_AFK_TIMEOUT_MS = 10_000;

export class ActivityTracker {
  private readonly config: Required<Pick<ActivityTrackerConfig, 'afkTimeoutMs'>> & ActivityTrackerConfig;
  private peers = new Map<string, PeerActivity>();

  constructor(config: Partial<ActivityTrackerConfig> = {}) {
    this.config = {
      afkTimeoutMs: config.afkTimeoutMs ?? DEFAULT_AFK_TIMEOUT_MS,
      ...config,
    };
  }

  /**
   * Record input activity for a peer, resetting AFK timers.
   */
  public recordActivity(peerId: string): void {
    const now = Date.now();
    let activity = this.peers.get(peerId);

    if (!activity) {
      activity = { afk: false, lastActivityAt: now, afkTimer: null, kickTimer: null };
      this.peers.set(peerId, activity);
    }

    const wasAfk = activity.afk;
    activity.lastActivityAt = now;
    activity.afk = false;

    this.clearTimers(activity);

    if (wasAfk) {
      this.config.onAfkChanged?.(peerId, false);
    }

    activity.afkTimer = setTimeout(() => {
      this.onAfkTimeout(peerId);
    }, this.config.afkTimeoutMs);
  }

  /**
   * Check if a peer is currently AFK.
   */
  public isAfk(peerId: string): boolean {
    return this.peers.get(peerId)?.afk ?? false;
  }

  /**
   * Get activity state for all tracked peers.
   */
  public getActivityState(): Record<string, { afk: boolean; lastActivityAt: number }> {
    const result: Record<string, { afk: boolean; lastActivityAt: number }> = {};
    for (const [peerId, activity] of this.peers.entries()) {
      result[peerId] = { afk: activity.afk, lastActivityAt: activity.lastActivityAt };
    }
    return result;
  }

  /**
   * Remove a peer and clean up all its timers.
   */
  public removePeer(peerId: string): void {
    const activity = this.peers.get(peerId);
    if (!activity) return;

    this.clearTimers(activity);
    this.peers.delete(peerId);
  }

  /**
   * Destroy all tracked peers and timers.
   */
  public destroy(): void {
    for (const activity of this.peers.values()) {
      this.clearTimers(activity);
    }
    this.peers.clear();
  }

  private onAfkTimeout(peerId: string): void {
    const activity = this.peers.get(peerId);
    if (!activity || activity.afk) return;

    activity.afk = true;
    this.config.onAfkChanged?.(peerId, true);

    if (this.config.kickTimeoutMs != null && this.config.onKick) {
      activity.kickTimer = setTimeout(() => {
        const current = this.peers.get(peerId);
        if (current?.afk) {
          this.config.onKick?.(peerId);
        }
      }, this.config.kickTimeoutMs);
    }
  }

  private clearTimers(activity: PeerActivity): void {
    if (activity.afkTimer) {
      clearTimeout(activity.afkTimer);
      activity.afkTimer = null;
    }
    if (activity.kickTimer) {
      clearTimeout(activity.kickTimer);
      activity.kickTimer = null;
    }
  }
}
